import type { SendOutcome } from "./patientSyncManager";

function containsErrorCode(
  value: unknown,
  expected: string,
  seen = new Set<unknown>(),
): boolean {
  if (typeof value === "string") return value.includes(expected);
  if (!value || typeof value !== "object" || seen.has(value)) return false;
  seen.add(value);
  if (Array.isArray(value)) {
    return value.some((item) => containsErrorCode(item, expected, seen));
  }
  return Object.entries(value as Record<string, unknown>).some(
    ([key, item]) =>
      ["code", "error", "message", "reason", "type"].includes(key) &&
      containsErrorCode(item, expected, seen),
  );
}

/**
 * Convert the deployed patient PATCH response taxonomy into queue behavior.
 * Messages are intentionally static: API validation bodies may echo sensitive
 * Medical History values and must never enter retry logs or durable metadata.
 */
export function classifyPatientSyncOutcome(result: {
  error?: unknown;
  response?: Pick<Response, "status">;
}): SendOutcome {
  if (result.error === undefined) return { kind: "delivered" };
  const status = result.response?.status ?? 0;

  // The deployed #1009 contract serializes duplicates and only returns a 409
  // for key/body reuse. Keep the older in-progress code retryable for rolling
  // compatibility without persisting/logging the server error body.
  if (
    status === 409 &&
    (containsErrorCode(result.error, "idempotency_key_in_progress") ||
      containsErrorCode(result.error, "idempotency_in_progress"))
  ) {
    return {
      kind: "transient",
      message: "Sync is temporarily unavailable.",
    };
  }
  if (status === 401 || status === 403) {
    return {
      kind: "permanent",
      reason: "auth",
      message: "Sign in again to sync this answer.",
    };
  }
  if (status === 404 || status === 410) {
    return {
      kind: "permanent",
      reason: "identity",
      message: "This patient session is no longer available.",
    };
  }
  if (status === 409) {
    return {
      kind: "permanent",
      reason: "conflict",
      message: "This answer could not be reconciled automatically.",
    };
  }
  // Timeouts and throttling are delivery conditions, not answer problems —
  // retry with backoff instead of parking the write as a validation failure.
  if (status === 408 || status === 429) {
    return { kind: "transient", message: "Sync is temporarily unavailable." };
  }
  if (status >= 400 && status < 500) {
    return {
      kind: "permanent",
      reason: "validation",
      message: "This answer needs review before it can sync.",
    };
  }
  return { kind: "transient", message: "Sync is temporarily unavailable." };
}
